import { useLayoutEffect, useRef } from 'react';

/**
 * Shared mount + org-switch tracking guard for settings cards that fire an
 * async save and need to gate their toast/error feedback on whether the
 * component is still mounted and the user is still on the org that started
 * the request.
 *
 * Both refs are synced via `useLayoutEffect`, not `useEffect`. This is
 * required, not a performance micro-optimization: `useLayoutEffect` runs
 * synchronously within the commit phase, before an in-flight save's awaited
 * continuation (a microtask that resolves after paint) can observe the change.
 *
 * - `currentOrgIdRef`: a passive `useEffect` sync leaves a window right after
 *   an org switch commits where the ref still holds the previous org id,
 *   letting a save's completion pass the
 *   `currentOrgIdRef.current === requestOrgId` guard and surface feedback
 *   against the newly selected org instead of being suppressed.
 * - `isMountedRef`: a passive `useEffect` cleanup runs after paint, so when a
 *   card unmounts while a save is settling the cleanup can run *after* the
 *   awaited continuation — leaving `isMountedRef.current` momentarily `true`
 *   and letting stale toast/error UI and state setters fire for an unmounted
 *   card. A `useLayoutEffect` cleanup invalidates the guard synchronously
 *   during the unmount commit, closing that window.
 *
 * Do NOT "simplify" either to `useEffect` — that silently reintroduces the race.
 */
export function useMountedOrgGuard(currentOrgId: string | number | undefined) {
  const isMountedRef = useRef(true);
  const currentOrgIdRef = useRef(currentOrgId);

  useLayoutEffect(() => {
    currentOrgIdRef.current = currentOrgId;
  }, [currentOrgId]);

  useLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return { isMountedRef, currentOrgIdRef };
}
